import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Assessment, EvidenceFile, Paginated } from '@grc/types';
import { apiFetch, apiUrl } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatBytes,
  formatDateTime,
} from '../../components/ui.js';

const PAGE_SIZE = 50;

export function EvidencePage() {
  const { org } = useOrg();
  const [assessmentId, setAssessmentId] = useState('');
  const [retention, setRetention] = useState('');
  const [offset, setOffset] = useState(0);

  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
  });
  const evidence = useQuery({
    queryKey: ['evidence-library', org.orgId, assessmentId, retention, offset],
    queryFn: () =>
      apiFetch<Paginated<EvidenceFile>>(`/orgs/${org.orgId}/evidence`, {
        query: {
          limit: PAGE_SIZE,
          offset,
          assessmentId: assessmentId || undefined,
          retentionStatus: retention || undefined,
        },
      }),
  });

  if (evidence.isLoading && offset === 0) return <LoadingPane label="Loading evidence library" />;
  const items = evidence.data?.items ?? [];
  const total = evidence.data?.total ?? 0;
  const assessmentById = new Map((assessments.data ?? []).map((a) => [a.id, a]));

  return (
    <div>
      <PageHeader
        title="Evidence library"
        subtitle="Every file uploaded across your assessments and corpus, with integrity hashes"
      />

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Select
          aria-label="Filter by assessment"
          value={assessmentId}
          onChange={(e) => {
            setAssessmentId(e.target.value);
            setOffset(0);
          }}
          style={{ width: 'auto', minWidth: 220 }}
        >
          <option value="">All assessments</option>
          {(assessments.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by retention status"
          value={retention}
          onChange={(e) => {
            setRetention(e.target.value);
            setOffset(0);
          }}
          style={{ width: 'auto' }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="superseded">Superseded</option>
          <option value="deleted">Deleted</option>
          <option value="retained">Retained</option>
        </Select>
      </div>

      <Table
        head={
          <>
            <th>File</th>
            <th>Linked to</th>
            <th>Size</th>
            <th>Uploaded</th>
            <th>Status</th>
          </>
        }
      >
        {items.map((e) => (
          <tr key={e.id}>
            <td>
              <a href={apiUrl(`/evidence/${e.id}/download`)}>{e.fileName}</a>
              <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                sha256 {e.sha256.slice(0, 16)}…
              </div>
            </td>
            <td>
              {e.responseId && e.assessmentId ? (
                <Link to={`/assessments/${e.assessmentId}/responses/${e.responseId}`}>
                  {assessmentById.get(e.assessmentId)?.name ?? 'Assessment response'}
                </Link>
              ) : e.corpusItemId ? (
                <Link to={`/corpus/${e.corpusItemId}`}>Corpus item</Link>
              ) : (
                '—'
              )}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>{formatBytes(e.sizeBytes)}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.uploadedAt)}</td>
            <td>
              <Badge tone={e.retentionStatus === 'active' ? 'success' : 'neutral'}>
                {e.retentionStatus}
              </Badge>{' '}
              {e.locked && <Badge>locked</Badge>}{' '}
              {e.sourceEvidenceId && (
                <span title="Attached from another response or corpus item">
                  <Badge tone="info">shared</Badge>
                </span>
              )}
            </td>
          </tr>
        ))}
      </Table>
      {items.length === 0 && (
        <p style={{ color: 'var(--color-text-muted)', padding: 16 }}>No evidence matches these filters.</p>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
